import { type PuzzleCore } from "./types.ts";

export interface PlatformerJumpConfig extends Record<string, unknown> {
  gravityMin: number;
  gravityMax: number;
  velocityMin: number;
  velocityMax: number;
  obstacleDistance: number;
  obstacleHeight: number;
  landingMin: number;
  landingMax: number;
  horizontalSpeed: number;
  simulationStep: number;
}

export interface PlatformerJumpInstance {
  obstacleDistance: number;
  obstacleHeight: number;
  landingMin: number;
  landingMax: number;
}

export interface PlatformerJumpSubmission extends Record<string, unknown> {
  gravity: number;
  launchVelocity: number;
}

export function jumpHeightAtDistance(
  distance: number,
  gravity: number,
  launchVelocity: number,
  horizontalSpeed: number,
) {
  const time = distance / horizontalSpeed;
  return launchVelocity * time - 0.5 * gravity * time * time;
}

export const platformerJumpCore: PuzzleCore<
  PlatformerJumpConfig,
  PlatformerJumpInstance,
  PlatformerJumpSubmission
> = {
  id: "platformer-jump",
  schemaVersion: 1,
  generate(config) {
    return {
      obstacleDistance: config.obstacleDistance,
      obstacleHeight: config.obstacleHeight,
      landingMin: config.landingMin,
      landingMax: config.landingMax,
    };
  },
  validate(config, instance, submission) {
    const obstacleClearance =
      jumpHeightAtDistance(
        instance.obstacleDistance,
        submission.gravity,
        submission.launchVelocity,
        config.horizontalSpeed,
      ) - instance.obstacleHeight;
    const totalFlightTime = (2 * submission.launchVelocity) / submission.gravity;
    const landingDistance = totalFlightTime * config.horizontalSpeed;
    const landed =
      landingDistance >= instance.landingMin &&
      landingDistance <= instance.landingMax;
    const success = obstacleClearance >= 0 && landed;
    return {
      success,
      score: success
        ? 100
        : Math.max(
            0,
            Math.round(70 + Math.min(0, obstacleClearance) * 10 - Math.abs(landingDistance - instance.landingMin)),
          ),
      feedback: success
        ? "The jump feels crisp: clean obstacle clearance and a stable landing."
        : obstacleClearance < 0
          ? `The hero clipped the obstacle by ${Math.abs(obstacleClearance).toFixed(1)}m.`
          : `The arc landed at ${landingDistance.toFixed(1)}m, outside the safe platform.`,
      details: { obstacleClearance, landingDistance },
    };
  },
};
